/**
 * @file ContentPropertiesMultimedia.js
 * @description Properties panel for multimedia container content configuration.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { FileDrop } from '../helpers/FileDrop';
import RichTextEditor from './RichTextEditor';
import './PropertiesMultimedia.css';
import Toast from '../ui/toasts/Toast';
import { uploadFile } from '../../services/containerService';
import {
    formatFileSize,
    getAudioLength,
    getFileCategory,
    getImageDimensions,
    getVideoRuntime,
    sanitizeFileName,
} from '../helpers/FileUtils';
import {
    createAsset,
    fetchAssets,
    fetchAssetByID,
} from '../../services/clientService';
import { InputField, SwitchField } from '../helpers/FormComponents';
import Tooltip from '../ui/tooltips/Tooltip';
import TagInput from '../helpers/TagInput';
import styles from '../assets/AssetLibrary.module.scss';
import AssetGridView from '../assets/components/AssetGridView';
import PaginationComponent from '../helpers/PaginationComponent';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import AddFromAssetsModal from '../ui/modals/AddFromAssetsModal';
import { isValidFileTypeAndSize } from '../../utils/validate';
import Spinner from '../ui/spinners/Spinner';
import { useAuthContext, useUploadContext } from '../../store/Contexts';
import { SecurityAttributes } from '../../utils/data/enums';
import { getText } from '../../i18n';

function ContentPropertiesMultimedia({
    item,
    setItem,
    setUploadFile,
    handleUploadFromDevice,
    handleAddFromAssets,
    setIsSaving,
    contentState,
    setContentState,
    portalId,
}) {
    const [areInputsDisabled, setAreInputsDisabled] = useState(false);
    const [file, setFile] = useState(null);
    const [isAddFromAssetsChecked, setIsAddFromAssetsChecked] = useState('');
    const [isUploadFromDeviceChecked, setIsUploadFromDeviceChecked] =
        useState('');
    const [asset, setAsset] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [assets, setAssets] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeCard, setActiveCard] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const latestRequestIdRef = useRef(0);
    const { text, textAssets, isInitialized } = contentState;
    const [editorKey, setEditorKey] = useState(0);
    const [editorInitialContent, setEditorInitialContent] = useState(null);
    const [isEditorReady, setIsEditorReady] = useState(false);
    const { hasSecurityAttribute } = useAuthContext();
    const { enqueueUpload } = useUploadContext();

    useEffect(() => {
        if (!isInitialized) {
            setIsEditorReady(false);
            setEditorInitialContent(null);
            return;
        }

        if (isEditorReady) {
            return;
        }

        const timer = setTimeout(() => {
            setEditorInitialContent(text);
            setEditorKey(prev => prev + 1);
            setIsEditorReady(true);
        }, 200);

        return () => clearTimeout(timer);
    }, [isInitialized, text, isEditorReady]);

    useEffect(() => {
        return () => {
            setIsEditorReady(false);
            setEditorInitialContent(null);
        };
    }, []);

    function updateContentState(updates) {
        setContentState(prev => ({ ...prev, ...updates }));
    }

    function setText(newText) {
        updateContentState({ text: newText });
    }

    function setTextAssets(updater) {
        if (typeof updater === 'function') {
            setContentState(prev => ({
                ...prev,
                textAssets: updater(prev.textAssets),
            }));
            return;
        }
        updateContentState({ textAssets: updater });
    }

    function extractTextFromHTML(htmlString) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;
        return tempDiv.textContent || tempDiv.innerText || '';
    }

    function replacePlaceholderWithTokens(content) {
        let newText = content;
        let currentContent = content;
        const placeholderQRUrl = `<img src="https://ingaged-dev-ingress.ext.dv.aws-uset1.oss2.mrshmc.com/v1/files/asset/150/PlaceholderQR_20241118_214050615985.png" style="width: fit-content; height: auto; cursor: pointer; margin: 0px auto;" draggable="true">`;
        const index = currentContent.indexOf(placeholderQRUrl);

        if (index !== -1) {
            newText = currentContent.replace(placeholderQRUrl, '[IndivRegQR]');
            currentContent = newText;
        }

        const regex = /<span style="color: blue"><u>(.*?)<\/u><\/span>/g;
        const match = regex.exec(currentContent);
        if (match) {
            const extractedText = extractTextFromHTML(match[0]);
            newText = currentContent.replace(
                match[0],
                `[IndivRegLink DisplayText="${extractedText}"]`,
            );
        }

        setText(newText);
    }

    useEffect(() => {
        const placeholderQRUrl = `<img src="https://ingaged-dev-ingress.ext.dv.aws-uset1.oss2.mrshmc.com/v1/files/asset/150/PlaceholderQR_20241118_214050615985.png" style="width: fit-content; height: auto; cursor: pointer; margin: 0px auto;" draggable="true">`;
        const hasPlaceholderQR =
            typeof text === 'string' && text.indexOf(placeholderQRUrl) !== -1;
        const hasPlaceholderLink =
            typeof text === 'string' &&
            text.search(/<span style="color: blue"><u>(.*?)<\/u><\/span>/) !==
                -1;

        if (hasPlaceholderQR || hasPlaceholderLink) {
            replacePlaceholderWithTokens(text);
        }

        setItem(prevState => {
            const { multimediaPart } = prevState;
            return {
                ...prevState,
                multimediaPart: {
                    ...multimediaPart,
                    text,
                    textType: 'html',
                },
            };
        });
    }, [text]);

    useEffect(() => {
        if (!isInitialized) {
            return;
        }

        setItem(prevState => {
            const existingNonTextAssets = (
                prevState.containerAssets || []
            ).filter(assetItem => {
                if (!assetItem || !assetItem.url) {
                    return false;
                }
                const multimediaText = prevState.multimediaPart?.text;
                const isInText =
                    typeof multimediaText === 'string' &&
                    multimediaText.includes(assetItem.url);
                return !isInText;
            });

            const combinedAssets = [...existingNonTextAssets, ...textAssets];

            return {
                ...prevState,
                containerAssets: combinedAssets,
            };
        });
    }, [textAssets, isInitialized]);

    useEffect(() => {
        if (isInitialized) {
            return;
        }

        async function fetchInitial() {
            setIsLoading(true);

            if (item === undefined) {
                updateContentState({ isInitialized: true });
                setIsLoading(false);
                return;
            }

            if ('templateStatus' in item && item.templateStatus === -1) {
                setAreInputsDisabled(true);
            }

            let initialText = '';
            if (
                'multimediaPart' in item &&
                item.multimediaPart !== null &&
                item.multimediaPart.text !== null &&
                item.multimediaPart.text !== ''
            ) {
                initialText = item.multimediaPart.text;
            }

            if (!('containerAssets' in item) || item.containerAssets === null) {
                updateContentState({
                    text: initialText,
                    isInitialized: true,
                });
                setIsLoading(false);
                return;
            }

            item.containerAssets = item.containerAssets.filter(
                assetItem => Object.keys(assetItem).length > 0,
            );

            const tempTextAssets = [];
            const orderedContainerAssets = [...item.containerAssets].sort(
                (a, b) => a.assetOrder - b.assetOrder,
            );

            for (const containerAsset of orderedContainerAssets) {
                if (!containerAsset.assetId) {
                    continue;
                }

                const fetchedAsset = await fetchAssetByID(
                    containerAsset.assetId,
                    null,
                );
                containerAsset.url = fetchedAsset.url;

                const multimediaText = item.multimediaPart?.text;
                const isInText =
                    typeof multimediaText === 'string' &&
                    multimediaText.indexOf(containerAsset.url) !== -1;

                if (!isInText) {
                    continue;
                }

                const regex = new RegExp(
                    `(<img[^>]+src=["']${containerAsset.url}["'][^>]+style=["']([^"']+)["'][^>]*>)`,
                    'i',
                );
                const match = multimediaText.match(regex);
                let width = null;
                let margin = null;

                if (match && match[2]) {
                    const style = match[2];

                    const widthMatch = style.match(/width:\s*([^;]+)/);
                    if (widthMatch) {
                        width = widthMatch[1].trim();
                    }

                    const marginMatch = style.match(/margin:\s*([^;]+)/);
                    if (marginMatch) {
                        margin = marginMatch[1].trim();
                    }
                }

                tempTextAssets.push({
                    id: containerAsset.id,
                    assetId: containerAsset.assetId,
                    url: containerAsset.url,
                    width,
                    margin,
                });
            }

            updateContentState({
                text: initialText,
                textAssets: tempTextAssets,
                isInitialized: true,
            });
            setIsLoading(false);
        }

        fetchInitial();
    }, [isInitialized]);

    function handleFileChange(fileValue) {
        setFile(sanitizeFileName(fileValue));
    }

    async function uploadAsset() {
        setIsSaving(true);

        if (!validateForm()) {
            setIsSaving(false);
            return;
        }

        let urlResponse;
        let isValidUrl = false;

        if (!asset.url) {
            try {
                const response = await enqueueUpload(file.name, uploadOptions =>
                    uploadFile(file, uploadOptions),
                );
                if (response.error) {
                    Toast.error(response.message);
                    setIsSaving(false);
                    return;
                }
                urlResponse = encodeURI(response);
                try {
                    new URL(urlResponse);
                    isValidUrl = true;
                    setAsset(prev => ({
                        ...prev,
                        url: urlResponse,
                    }));
                } catch {
                    Toast.error(getText('msg_3e298875de25'));
                    setIsSaving(false);
                    return;
                }
            } catch {
                Toast.error(getText('msg_3e298875de25'));
                setIsSaving(false);
                return;
            }
        }

        if (!isValidUrl && !asset.url) {
            setIsSaving(false);
            return;
        }

        const newAsset = { ...asset };
        if (!newAsset.assetTypeId) {
            newAsset.assetTypeId = 4;
        }
        if (urlResponse) {
            newAsset.url = urlResponse;
        }
        newAsset.fileType = file.name.split('.').pop();
        newAsset.fileSize = formatFileSize(file.size);

        const fileCategory = getFileCategory(file.name);
        const metadataHandlers = {
            video: () => getVideoRuntime(file),
            audio: () => getAudioLength(file),
            image: () => getImageDimensions(file),
        };

        if (fileCategory in metadataHandlers) {
            newAsset.metaData = await metadataHandlers[fileCategory]();
        }

        const createResponse = await createAsset(newAsset, null);

        if (createResponse.error) {
            Toast.error(createResponse.message);
            setIsSaving(false);
            return;
        }

        if (createResponse.status === 200 && createResponse.data.url) {
            Toast.success(getText('msg_9309271e92fe'));
            handleAssetUploadSuccess(createResponse.data);
            setIsUploadFromDeviceChecked('');
            setAsset({});
            setFile(null);
            setIsSaving(false);
            return;
        }

        Toast.error(getText('msg_9f8fd8f97fd3'));
        setIsSaving(false);
    }

    function handleAssetUploadSuccess(responseData) {
        if (isUploadFromDeviceChecked !== 'text') {
            return;
        }

        setTextAssets(prevTextAssets => {
            const assetExists = prevTextAssets.some(
                assetItem => assetItem.assetId === responseData.id,
            );
            if (assetExists) {
                return prevTextAssets;
            }
            return [
                ...prevTextAssets,
                {
                    assetId: responseData.id,
                    assetTypeId: responseData.assetTypeId,
                    url: responseData.url,
                },
            ];
        });
    }

    function validateForm() {
        if (!asset.name || asset.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }

        if (!file) {
            Toast.error(getText('msg_e11c5988bd25'));
            return false;
        }

        const fileTypeAndSizeValidation = isValidFileTypeAndSize(file);
        if (fileTypeAndSizeValidation) {
            Toast.error(fileTypeAndSizeValidation);
            return false;
        }

        if (asset.name.length > 256) {
            Toast.error(getText('msg_f4231db74e51'));
            return false;
        }

        return true;
    }

    function updateAsset(assetObj, key, value) {
        return { ...assetObj, [key]: value };
    }

    const loadAssets = useCallback(
        async (page = currentPage, search = searchTerm) => {
            latestRequestIdRef.current += 1;
            const requestId = latestRequestIdRef.current;

            setIsLoading(true);
            setIsSaving(true);

            try {
                let queryParams = {
                    assetTypes: [1, 2],
                    page,
                    pageSize: 12,
                };

                if (
                    isAddFromAssetsChecked === 'image' ||
                    isAddFromAssetsChecked === 'text'
                ) {
                    queryParams = {
                        ...queryParams,
                        fileTypes: ['jpg', 'jpeg', 'png', 'svg'],
                    };
                }

                if (isAddFromAssetsChecked === 'video') {
                    queryParams = { ...queryParams, fileTypes: ['mp4', 'mov'] };
                }

                if (search && search.trim() !== '') {
                    queryParams = { ...queryParams, search: search.trim() };
                }

                const fetchedAssets = await fetchAssets(
                    queryParams,
                    null,
                    false,
                );

                if (requestId !== latestRequestIdRef.current) {
                    return;
                }

                if (!fetchedAssets) {
                    setAssets([]);
                    setTotalPages(1);
                    return;
                }

                setAssets(fetchedAssets.viewModels || []);
                setTotalPages(fetchedAssets.pageTotal || 1);
            } catch (error) {
                console.error('Failed to fetch assets', error);
                Toast.error(getText('msg_cade149a85b2'));
            } finally {
                if (latestRequestIdRef.current === requestId) {
                    setIsLoading(false);
                    setIsSaving(false);
                }
            }
        },
        [currentPage, searchTerm, isAddFromAssetsChecked],
    );

    const debouncedLoadRef = useRef(
        debounce(term => {
            setCurrentPage(1);
            loadAssets(1, term);
        }, 400),
    );

    useEffect(() => {
        const debounced = debouncedLoadRef.current;
        return () => {
            debounced.cancel();
        };
    }, []);

    function handleSearch(event) {
        const value = event.target.value;
        setSearchTerm(value);

        debouncedLoadRef.current(value);

        if (value === '') {
            debouncedLoadRef.current.cancel();
            setCurrentPage(1);
            loadAssets(1, '');
        }
    }

    function handleSearchKeyDown(event) {
        if (event.key !== 'Enter') {
            return;
        }
        const debounced = debouncedLoadRef.current;
        debounced.cancel();
        setCurrentPage(1);
        loadAssets(1, searchTerm);
    }

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets(currentPage, searchTerm);
        }
    }, [isAddFromAssetsChecked]);

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets(currentPage, searchTerm);
        }
    }, [currentPage]);

    function handleClick(index) {
        setActiveCard(activeCard === index ? null : index);
    }

    function addAsset() {
        if (activeCard === null) {
            Toast.error(
                getText('msg_c9d0d3fc9172'),
                null,
                getText('msg_40f0bdba0938'),
            );
            return;
        }

        const assetAdding = assets[activeCard];
        handleAssetAddFromLibrary(assetAdding);
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
    }

    function handleAssetFromLibrarySelected(assetAdding) {
        handleAssetAddFromLibrary(assetAdding);
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
    }

    function getAcceptedFileTypes() {
        if (isAddFromAssetsChecked === 'video') {
            return ['mp4', 'mov'];
        }

        return ['jpg', 'jpeg', 'png', 'svg'];
    }

    function handleAssetAddFromLibrary(assetAdding) {
        if (isAddFromAssetsChecked !== 'text') {
            return;
        }

        setTextAssets(prevTextAssets => {
            const assetExists = prevTextAssets.some(
                assetItem => assetItem.assetId === assetAdding.id,
            );
            if (assetExists) {
                return prevTextAssets;
            }
            return [
                ...prevTextAssets,
                { assetId: assetAdding.id, url: assetAdding.url },
            ];
        });
    }

    function handleAssetDelete(assetId) {
        setTextAssets(prevAssets =>
            prevAssets.filter(assetItem => assetItem.assetId !== assetId),
        );
    }

    const handleContentUpdate = useCallback(
        newText => {
            setContentState(prev => {
                if (prev.text === newText) {
                    return prev;
                }
                return { ...prev, text: newText };
            });
        },
        [setContentState],
    );

    function shouldHideMainContent() {
        return isUploadFromDeviceChecked || isAddFromAssetsChecked;
    }

    if (isLoading) {
        return <Spinner width={50} className={styles.loadingSpinner} />;
    }

    return (
        <>
            <div
                style={{
                    display: shouldHideMainContent() ? 'none' : 'flex',
                    alignItems: 'center',
                    lineHeight: '28px',
                }}
            >
                <span
                    style={{
                        color: '#2F2B43',
                        fontWeight: '500',
                        fontSize: '16px',
                    }}
                >
                    {getText('msg_c3328c39b0e2')}
                </span>
            </div>
            <div
                style={{
                    marginTop: '20px',
                    display:
                        areInputsDisabled || shouldHideMainContent()
                            ? 'none'
                            : 'block',
                }}
            >
                {!isLoading &&
                    isInitialized &&
                    isEditorReady &&
                    editorInitialContent !== null && (
                        <RichTextEditor
                            key={`editor-${editorKey}`}
                            initialContent={editorInitialContent}
                            onContentUpdate={handleContentUpdate}
                            onUploadFromDevice={setIsUploadFromDeviceChecked}
                            onAddFromAssets={setIsAddFromAssetsChecked}
                            textAssets={textAssets}
                            portalId={portalId}
                            onAssetDelete={handleAssetDelete}
                            disableHomeLink={true}
                        />
                    )}
            </div>

            {isUploadFromDeviceChecked && (
                <>
                    <div style={{ width: '100%', paddingTop: '6px' }}>
                        <FileDrop file={file} onChange={handleFileChange} />
                    </div>

                    <InputField
                        label={getText('msg_709a23220f2c')}
                        onChange={value =>
                            setAsset(updateAsset(asset, 'name', value))
                        }
                        maxChars={256}
                        required
                        placeholder={getText('msg_b7ffb9789f09')}
                        value={asset.name}
                    />

                    <div style={{ marginBottom: '24px' }}>
                        <div className="file-form-label">
                            {getText('msg_848eed0fbd54')}
                            <Tooltip
                                html="Use relevant and specific keywords to help make searching for an asset easier."
                                placement="right"
                            >
                                <i
                                    className="bi bi-info-circle"
                                    style={{
                                        paddingLeft: '5px',
                                        fontSize: '13px',
                                        color: 'var(--iNGAGED-Primary-Color)',
                                    }}
                                />
                            </Tooltip>
                        </div>
                        <TagInput
                            onTagsChange={tags =>
                                setAsset(updateAsset(asset, 'assetTags', tags))
                            }
                            savedTags={asset.tags}
                        />
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <SwitchField
                            label={getText('msg_0c359a10ae17')}
                            onChange={isChecked =>
                                setAsset(
                                    updateAsset(
                                        asset,
                                        'assetTypeId',
                                        isChecked ? 4 : 5,
                                    ),
                                )
                            }
                            tooltip={getText('msg_5abd905257ee')}
                            checked={asset.assetTypeId !== 5}
                        />
                    </div>

                    <div
                        id="footer"
                        style={{
                            height: 'fit-content',
                            width: '100%',
                            padding: '28px',
                        }}
                    >
                        <div
                            role="button"
                            tabIndex={0}
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-start button-base button-secondary"
                            onClick={() => setIsUploadFromDeviceChecked('')}
                        >
                            {getText('msg_77dfd2135f4d')}
                        </div>
                        <div
                            role="button"
                            tabIndex={0}
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-end button-base ing-button-primary-left"
                            onClick={uploadAsset}
                        >
                            {getText('msg_61cc55aa0453')}
                        </div>
                    </div>
                </>
            )}

            {isAddFromAssetsChecked && (
                <AddFromAssetsModal
                    title="Add from Library"
                    onYes={handleAssetFromLibrarySelected}
                    onNo={() => {
                        setIsAddFromAssetsChecked('');
                    }}
                    onHide={() => {
                        setIsAddFromAssetsChecked('');
                    }}
                    yesButtonText="Add"
                    noButtonText="Cancel"
                    acceptedFileTypes={getAcceptedFileTypes()}
                    assetTypes={[1, 2]}
                    wasCreatedByMe={false}
                    displayModalBackdrop={false}
                />
            )}
        </>
    );
}

export default ContentPropertiesMultimedia;
